import type { SceneNode, SceneDocument } from '@/lib/scene-document';

/**
 * Shared "render doc" builder for the CanvasKit editor surfaces.
 *
 * Both the standalone editor (useEditorRenderer) and the infinite-canvas
 * workspace (useWorkspaceRenderer) need the same pre-draw transform:
 *   1. Run the auto-layout pass so child positions are always resolved.
 *   2. When a reorder drag is active, splice the dragged node into its target
 *      slot (so siblings paint at their would-be positions) and override the
 *      dragged node's x/y to follow the cursor at reduced opacity.
 *
 * Pure: never mutates the input state or its doc.
 */
import { computeLayout } from './canvaskit-layout';
import { restackBands } from './canvas-band-restack';
import { getParentWorldOffset } from './canvaskit-hit-test';
import { resolveDocColorsMemo } from './canvas-brand-resolve';

import type { CanvasEditorStore } from './canvaskit-editor-store';

type EditorState = ReturnType<CanvasEditorStore['getState']>;

/**
 * If the doc has a single root wrapper whose laid-out dimensions differ
 * from the doc's own width/height (typically because the wrapper is HUG-
 * sized and just grew/shrunk to fit its content), update doc.width/height
 * so the canvas surface tracks the content. Pure — returns a new doc only
 * when needed. No-op when there are multiple root frames (the workspace),
 * where each frame keeps its own free position.
 */
export function syncDocSizeToRoot(doc: SceneDocument): SceneDocument {
  if (doc.rootChildren.length !== 1) return doc;
  const root = doc.nodes[doc.rootChildren[0]];
  if (!root) return doc;
  if (doc.width === root.width && doc.height === root.height) return doc;
  return { ...doc, width: root.width, height: root.height };
}

/**
 * Run the layout pass and, if a reorder drag is active, splice the dragged
 * node into its target slot (so siblings paint at their would-be positions)
 * and override the dragged node's x/y to follow the cursor with reduced
 * opacity. Returns a fresh doc — never mutates state.doc.
 */
export function buildRenderDoc(state: EditorState): SceneDocument {
  const { dragReorder, dragStart, dragNodeStarts } = state;

  // Resolve brand-token SOLID colors for display before layout. The store's
  // doc stays RAW (tokens preserved for persistence/undo/embed); only this
  // render-side clone carries hex. Memoized per doc identity + brand context,
  // and byte-identical (same object) for token-free scenes. Colors don't affect
  // layout, so resolving before computeLayout is safe.
  const doc = resolveDocColorsMemo(state.doc, state.brandContext);

  if (!dragReorder || !dragStart) return syncDocSizeToRoot(computeLayout(doc));

  const { parentId, draggedId, fromIndex, toIndex } = dragReorder;
  const parent = doc.nodes[parentId];
  if (!parent?.children) return computeLayout(doc);

  // Splice dragged into its target slot for the layout preview. Auto-layout
  // parents reflow via computeLayout below; absolute artboards get explicit
  // restacked y positions (computeLayout doesn't move NONE-layout children).
  let previewDoc = doc;
  if (toIndex !== fromIndex && dragReorder.absolute) {
    const patches = restackBands(doc, parentId, draggedId, toIndex);
    if (patches.size > 0) {
      const nextNodes: Record<string, SceneNode> = { ...doc.nodes };
      for (const [id, patch] of patches) {
        nextNodes[id] = { ...nextNodes[id], ...patch };
      }
      previewDoc = { ...doc, nodes: nextNodes };
    }
  } else if (toIndex !== fromIndex) {
    const previewChildren = [...parent.children];
    const removed = previewChildren.splice(fromIndex, 1);
    if (removed.length === 1) {
      previewChildren.splice(toIndex, 0, removed[0]);
      previewDoc = {
        ...doc,
        nodes: {
          ...doc.nodes,
          [parentId]: { ...parent, children: previewChildren },
        },
      };
    }
  }

  const laidOut = syncDocSizeToRoot(computeLayout(previewDoc));

  // Override the dragged node so it follows the cursor and renders at half
  // opacity. Convert the world-space drag delta into the dragged node's
  // parent-local space by subtracting the parent chain's accumulated offset.
  const startInfo = dragNodeStarts.get(draggedId);
  const draggedNode = laidOut.nodes[draggedId];
  if (!startInfo || !draggedNode) return laidOut;

  const parentOffset = getParentWorldOffset(laidOut, draggedId);
  const localX = startInfo.aabb.x + dragReorder.deltaX - parentOffset.x;
  const localY = startInfo.aabb.y + dragReorder.deltaY - parentOffset.y;

  return {
    ...laidOut,
    nodes: {
      ...laidOut.nodes,
      [draggedId]: {
        ...draggedNode,
        x: localX,
        y: localY,
        opacity: (draggedNode.opacity ?? 1) * 0.55,
      },
    },
  };
}
